<script setup lang="ts">
import DisplayDeliveryPlanInformation from '@/components/DailyReportEdit/Merge/DisplayDeliveryPlanInformation.vue';
import DailyReportMergeFormDetail from '@/components/DailyReportEdit/Merge/MergeFormDetail.vue';
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import getDailyReportEditStatus from '@/types/responses/getDailyReportEditStatus';
import divideDailyReportsResponse from '@/types/responses/divideDailyReports';
import RegisterModal from '@/components/DailyReportEdit/Merge/RegisterModal.vue';
import CompleteModal from '@/components/DailyReportEdit/Split/CompleteModal.vue';
import { useAuth } from '@/services/user/Auth0UserProvider';
import Api from '@/services/api/ApiServiceFabrick';
import { ref, Ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import i18n from '@/lang/i18n';

const { loginWithRedirect } = useAuth();
const router = useRouter();
const isLoading: Ref<boolean> = ref(false);
const isOpenDialog: Ref<boolean> = ref(false);
const isDisplayCompleteModal: Ref<boolean> = ref(false);
const deliveryPlanChildren = ref<getDailyReportEditStatus[]>([]);

const props = defineProps<{
  deliveryPlanId: string
  deliveryPlan: getDeliveryPlanVehicleInformation
  switchContentLoading: (isLoading: boolean) => void
  showFlash: (message: string, type: string, fadeout: boolean) => void
}>();

onMounted(() => {
  getDailyReportEditStatusChildren();
});

function getDailyReportEditStatusChildren() {
  Api().getDailyReportEditStatusChildren(props.deliveryPlanId).then((data: getDailyReportEditStatus[]) => {
    deliveryPlanChildren.value = data;
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    props.switchContentLoading(false);
  });
}

function execMerge() {
  isLoading.value = true;
  Api().mergeDailyReports(props.deliveryPlanId).then((data: divideDailyReportsResponse) => {
    if (data.status) {
      return isDisplayCompleteModal.value = true;
    }
    props.showFlash(i18n.global.t('pages.dailyReports.splitForm.failedMessage'), 'error', true);
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    isLoading.value = false;
  });
}

function errorHandler(error: any) {
  const res = error.response
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      location.reload()
      break
  }
}

function moveDailyReportDisplay() {
  router.push({ name: "DailyReport" })
}
</script>
<template>
  <div class="d-flex" style="min-height: 45vw">
    <div class="content p-4 w-100">
      <v-card class="my-4 pa-4">
        <h4 class="card-title mt-0 mb-4">
          {{ i18n.global.t('pages.dailyReports.mergeForm.title') }}
        </h4>
        <v-row>
          <DisplayDeliveryPlanInformation
            :deliveryPlan="props.deliveryPlan"
          />
          <DailyReportMergeFormDetail
            :deliveryPlanChildren="deliveryPlanChildren"
          />
        </v-row>
        <div
          class="d-flex justify-content-end m-3"
        >
          <v-btn
            class="ma-2 reset-btn"
            :disabled="isLoading"
            @click="moveDailyReportDisplay"
          >
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
          <v-btn
            class="ma-2 exec-btn"
            :loading="isLoading"
            @click="isOpenDialog = true"
          >
            {{ i18n.global.t('common.confirmBtn') }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
  <RegisterModal
    :title="i18n.global.t('pages.dailyReports.mergeConfirmModal.title')"
    v-model="isOpenDialog"
    :execFunction="execMerge"
  />
  <CompleteModal
    v-model="isDisplayCompleteModal"
    :messages="[
      i18n.global.t('pages.dailyReports.mergeCompleteModal.messageRow1'),
      i18n.global.t('pages.dailyReports.mergeCompleteModal.messageRow2')
    ]"
    :moveDailyReportDisplay="moveDailyReportDisplay"
  />
</template>
